import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

import { ensureValidToken } from '@/features/auth/utils/tokenRefresh'
import { API_URL } from '../shared/constants/config'

// Extend AxiosRequestConfig to include custom properties
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _skipAuth?: boolean
  }
}

export const api = axios.create({
  baseURL: API_URL,
})

type ApiSuccessEnvelope<T> = {
  success: true
  payload: T
  message?: string
  [key: string]: unknown
}

type ApiFailureEnvelope = {
  success: false
  payload?: unknown
  message?: string
  [key: string]: unknown
}

type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiFailureEnvelope

/**
 * Unwraps API envelope response format
 * Converts { success: true, payload: T } to T
 */
const unwrapApiEnvelope = <T>(response: AxiosResponse<T | ApiEnvelope<T>>): AxiosResponse<T> => {
  const rawData = response.data as ApiEnvelope<T> | T

  if (rawData && typeof rawData === 'object' && 'success' in rawData) {
    const envelope = rawData as ApiEnvelope<T>

    if (!envelope.success) {
      const error = new Error(envelope.message || 'API request failed')
      ;(error as Error & { response?: AxiosResponse }).response = response
      throw error
    }

    response.data = envelope.payload as T
  }

  return response as AxiosResponse<T>
}

/**
 * Request interceptor
 * - Adds authentication token to requests
 * - Ensures token is valid before making requests (proactive refresh)
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip auth for certain requests (like login, signup)
    if (config._skipAuth) {
      return config
    }

    // Get valid token (will refresh if expiring soon)
    const accessToken = await ensureValidToken()

    // Add token to header if available
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

/**
 * Response interceptor
 * - Unwraps API envelope format
 * - 401 errors are now handled by TanStack Query's global error handlers
 * - Handles version mismatch during deployment (auto-reload)
 */
api.interceptors.response.use(
  response => unwrapApiEnvelope(response),
  error => {
    // Check for chunk loading errors (deployment in progress)
    if (error?.message?.includes('Failed to fetch dynamically imported module')) {
      console.warn('Chunk loading error detected. Reloading page...')
      window.location.reload()
      return Promise.reject(error)
    }

    // Check for version mismatch (HTTP 409 Conflict)
    if (error?.response?.status === 409) {
      console.warn('Version mismatch detected. Reloading page...')
      window.location.reload()
      return Promise.reject(error)
    }

    // Simply reject the error - TanStack Query will handle retries and token refresh
    return Promise.reject(error)
  }
)
